<template>
  <div class="flex flex-col gap-4">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <PaymentFormInput
        v-model="form.ach_first_name"
        label="First name"
        :error-message="firstErrorMessage(v$.form.ach_first_name)"
        data-cy="ach-first-name"
      />
      <PaymentFormInput
        v-model="form.ach_last_name"
        label="Last name"
        :error-message="firstErrorMessage(v$.form.ach_last_name)"
        data-cy="ach-last-name"
      />
    </div>

    <PaymentFormSelect
      v-model="form.ach_account_type"
      label="Account type"
      placeholder="Select account type"
      :options="accountTypes"
      :error-message="firstErrorMessage(v$.form.ach_account_type)"
      data-cy="account-type"
    />

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <PaymentFormInput
        v-model="form.ach_routing_number"
        label="Routing number"
        inputmode="numeric"
        :error-message="firstErrorMessage(v$.form.ach_routing_number)"
        data-cy="ach-routing-number"
      />
      <PaymentFormInput
        v-model="form.ach_account_number"
        label="Account number"
        inputmode="numeric"
        :error-message="firstErrorMessage(v$.form.ach_account_number)"
        data-cy="ach-account-number"
      />
    </div>

    <SaveForFuturePayments
      v-if="canSaveCard"
      v-model="form.ach_save_payment_method"
      data-cy="ach-save-payment-method"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import PaymentFormInput from "@/components/PaymentModals/PaymentFormInput.vue";
import PaymentFormSelect from "@/components/PaymentModals/PaymentFormSelect.vue";
import SaveForFuturePayments from "@/components/PaymentModals/SaveForFuturePayments.vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import usePaymentMethods from "./usePaymentMethods";

interface ACHFormData {
  ach_first_name: string;
  ach_last_name: string;
  ach_account_type: string;
  ach_account_number: string;
  ach_routing_number: string;
  ach_save_payment_method: boolean;
}

const props = withDefaults(
  defineProps<{
    form: ACHFormData | null;
    autoSaveCard?: boolean;
  }>(),
  {
    autoSaveCard: false
  }
);

const { paymentMethodsResponse } = usePaymentMethods();

const form = computed(() => props.form!);

const accountTypes = [
  { label: "Checking", value: "checking" },
  { label: "Savings", value: "savings" }
];

const config = computed(() =>
  paymentMethodsResponse.value.find((method: any) => method.key === "gpi_ach")
);

const canSaveCard = computed(
  () => !!config.value?.card_savable && !props.autoSaveCard
);

const v$ = useVuelidate(
  {
    form: {
      ach_routing_number: { required, $autoDirty: true },
      ach_last_name: { required, $autoDirty: true },
      ach_account_type: { required, $autoDirty: true },
      ach_account_number: { required, $autoDirty: true }
    }
  },
  { form }
);
</script>
